import { VehicleFuelType } from '@api/index';
import { getFeatureToggles, getPerformVehicles } from '@common/permissions/selectors';
import { getSelectedDriverIds, getVehiclesByFuelType } from '@data/selectors';
import { enablePerform3 } from '@features/settings/reducer';
import { hideSidebar } from '@features/sidebar';
import { getActiveDateRange } from '@features/ui/selectors';
import _ from 'lodash';
import { useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import { Dispatch } from 'redux';

import { State } from '../../setup/types';
import { DateRange } from '../../types';
import DriverAnalysis from './DriverAnalysis';
import { DriverFuelType } from './driverAnalysisReducer';
import useAvailableFuelType from './hooks/useAvailableFuelType';
import useDriverContentLoader from './hooks/useDriverContentLoader';
import useSelectedFuelType from './hooks/useSelectedFuelType';

const Entry: React.FC<{
    selectedDriverIds: string[];
    vehicles: string[];
    activeDateRange: DateRange;
    hideSidebar: () => void;
    shouldShowHarshKPIs: boolean;
    shouldUseV2Ratings: boolean;
    shouldShowExcessiveKPIs: boolean;
    shouldUseV3Ratings: boolean;
}> = ({
    selectedDriverIds,
    vehicles,
    activeDateRange,
    hideSidebar,
    shouldShowHarshKPIs,
    shouldUseV2Ratings,
    shouldShowExcessiveKPIs,
    shouldUseV3Ratings,
}) => {
    useEffect(() => {
        if (selectedDriverIds.length === 0) {
            hideSidebar();
        }
    }, [hideSidebar, selectedDriverIds.length]);

    const { selectedFuelType } = useSelectedFuelType();
    const { setAvailableFuelType } = useAvailableFuelType();
    const dieselVehicles = useSelector(getVehiclesByFuelType(VehicleFuelType.DIESEL))
        .concat(useSelector(getVehiclesByFuelType(VehicleFuelType.UNKNOWN)))
        .map((v) => v.id);
    const electricVehicles = useSelector(getVehiclesByFuelType(VehicleFuelType.ELECTRIC)).map(
        (v) => v.id,
    );

    const electricPerformVehicles = _.intersection(vehicles, electricVehicles);
    const dieselPerformVehicles = _.intersection(vehicles, dieselVehicles);

    useEffect(() => {
        if (dieselPerformVehicles.length > 0 && electricPerformVehicles.length > 0) {
            setAvailableFuelType(DriverFuelType.ALL);
        } else if (dieselPerformVehicles.length > 0) {
            setAvailableFuelType(DriverFuelType.FUEL);
        } else if (electricPerformVehicles.length > 0) {
            setAvailableFuelType(DriverFuelType.ELECTRIC);
        }
    }, [dieselPerformVehicles.length, electricPerformVehicles.length]);

    let vehiclesToRequest: string[] = vehicles;
    if (selectedFuelType === DriverFuelType.ELECTRIC) {
        vehiclesToRequest = electricPerformVehicles;
    } else if (selectedFuelType === DriverFuelType.FUEL) {
        vehiclesToRequest = dieselPerformVehicles;
    }
    const { parentsWithChildren, attributes } = useDriverContentLoader({
        selectedDriverIds,
        vehicles: vehiclesToRequest,
        dateRange: activeDateRange,
        shouldShowHarshKPIs,
        shouldUseV2Ratings,
        shouldShowExcessiveKPIs,
        shouldUseV3Ratings,
    });
    return <DriverAnalysis parentsWithChildren={parentsWithChildren} attributes={attributes} />;
};

export const mapDispatchToProps = (dispatch: Dispatch) => ({
    hideSidebar: () => dispatch(hideSidebar()),
});

const mapStateToProps = (state: State) => ({
    selectedDriverIds: getSelectedDriverIds(state),
    vehicles: getPerformVehicles(state),
    activeDateRange: getActiveDateRange(state),
    shouldShowHarshKPIs: getFeatureToggles(state).showHarshKPIs as boolean,
    shouldUseV2Ratings: enablePerform3(state),
    shouldShowExcessiveKPIs: getFeatureToggles(state).showExcessiveKPIs as boolean,
    shouldUseV3Ratings: getFeatureToggles(state).newOvertakeModel as boolean,
});

export default connect(mapStateToProps, mapDispatchToProps)(Entry);
